/**
 * 学习数据 / 成绩复盘
 * ------------------------------------------------------------
 * 内容来源全部是 sessions / answers / errors / mastery 的聚合，
 * 历史成绩永久保留、永不覆盖：这里是"看得见的进步"。
 */
import { state, levelFromXp } from '../core/store.js';
import { listSessions, todayStats, studyStreak, dueErrors, unitMasteryRate, masteryOf, answersOf } from '../services/records.js';
import { achievementBoard } from '../services/rewards.js';
import { listUnits, getUnitContent } from '../services/dataSource.js';
import { card, statRow, lineChart, barChart, badgeWall, progressBar, emptyState, tag, masteryMatrix, openModal } from '../components/ui.js';
import { WORD_LEVELS } from '../engines/generators/wordLevels.js';
import { SENTENCE_LEVELS } from '../engines/generators/sentenceLevels.js';
import { escapeHtml, fmtDate, fmtDuration } from '../utils/index.js';

export default async function renderReport(mount) {
  const [sessions, today, streak, due, badges] = await Promise.all([
    listSessions(), todayStats(), studyStreak(), dueErrors(), achievementBoard(),
  ]);
  const lv = levelFromXp(state.student?.xp || 0);

  if (!sessions.length) {
    mount.innerHTML = `<div class="page-head"><h1>📊 学习数据</h1></div>
      ${card(emptyState('📊', '还没有学习记录', '完成第一次练习后，这里会出现成绩曲线、掌握矩阵和徽章墙',
        '<a class="btn btn-primary" href="#/practice">去练习</a>'))}`;
    return;
  }

  // 成绩曲线（按时间正序）
  const trend = sessions.slice(0, 15).reverse().map(s => ({ label: fmtDate(s.endedAt), value: s.score }));

  // 模块正确率
  const moduleAcc = ['word', 'sentence', 'grammar', 'comprehensive', 'game'].map(m => {
    const rows = sessions.filter(s => s.module === m);
    const total = rows.reduce((n, s) => n + (s.total || 0), 0);
    const correct = rows.reduce((n, s) => n + (s.correct || 0), 0);
    return { label: moduleName(m), value: total ? Math.round((correct / total) * 100) : 0, cls: m };
  }).filter(x => x.value > 0);

  // 最高分 / 平均分
  const scores = sessions.map(s => s.score || 0);
  const best = Math.max(...scores);
  const avg = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  const unlockedCount = badges.filter(b => b.unlocked).length;

  mount.innerHTML = `
    <div class="page-head">
      <h1>📊 学习数据</h1>
      <p class="lead">共 ${sessions.length} 次练习记录，全部永久保留（不会被新成绩覆盖）。</p>
    </div>

    ${card(statRow([
      { label: '当前等级', value: `Lv.${lv.level}` },
      { label: '累计经验', value: lv.xp, unit: ' XP' },
      { label: '最高分', value: best },
      { label: '平均分', value: avg },
      { label: '连续学习', value: streak, unit: ' 天' },
      { label: '已解锁成就', value: `${unlockedCount}/${badges.length}` },
    ]), {})}

    <div class="split mt-8">
      <div class="col" style="gap:16px">
        ${card(`
          <div class="chart-card">${lineChart(trend)}</div>
          <div class="chart-legend"><span><i style="background:var(--c-primary)"></i>最近 15 次练习得分</span></div>`,
          { title: '进步曲线', extra: sessions.length > 15 ? tag('仅显示最近 15 次', '') : '' })}

        ${card(barChart(moduleAcc), { title: '各模块正确率' })}

        ${card(`
          <div class="table-wrap"><table class="tbl">
            <thead><tr><th>时间</th><th>类型</th><th>题目</th><th>得分</th><th>正确率</th><th>用时</th><th></th></tr></thead>
            <tbody>
              ${sessions.slice(0, 30).map(s => `
                <tr>
                  <td class="muted nowrap">${fmtDate(s.endedAt, true)}</td>
                  <td>${escapeHtml(s.title || moduleName(s.module))}</td>
                  <td>${s.total}</td>
                  <td><b class="${s.score >= 80 ? 'sym-ok' : s.score >= 60 ? 'sym-hint' : 'sym-no'}">${s.score}</b></td>
                  <td>${s.accuracy}%</td>
                  <td class="muted">${fmtDuration(s.durationMs)}</td>
                  <td><button class="btn btn-sm btn-ghost" data-detail="${s.id}" type="button">详情</button></td>
                </tr>`).join('')}
            </tbody>
          </table></div>
          ${sessions.length > 30 ? `<p class="muted mt-8" style="font-size:12px">仅显示最近 30 条，共 ${sessions.length} 条记录。</p>` : ''}`,
          { title: '历史成绩' })}
      </div>

      <div class="col" style="gap:16px">
        ${card(badgeWall(badges), { title: '徽章墙', extra: tag(`${unlockedCount}/${badges.length}`, 'green') })}
        ${card(
          due.length
            ? `<p class="muted" style="font-size:12.5px">共 ${due.length} 个知识点待复习</p>
               <a class="btn btn-primary btn-block" href="#/review">去复习</a>`
            : '<p class="sym-ok">✓ 没有待复习的知识点</p>',
          { title: '复习状态' })}
      </div>
    </div>`;

  /* 详情弹窗 */
  mount.querySelectorAll('[data-detail]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const s = sessions.find(x => x.id === btn.dataset.detail);
      const answers = await answersOf(s.id);
      openModal({
        title: `练习详情 · ${s.title || moduleName(s.module)}`,
        size: 'wide',
        body: `<div class="stat-row" style="margin-bottom:14px">
            ${statRow([
              { label: '得分', value: s.score },
              { label: '题量', value: s.total },
              { label: '独立完成', value: s.independent || 0 },
              { label: '提示后', value: s.hinted || 0 },
              { label: '未完成', value: s.failed || 0 },
            ])}
          </div>
          <div class="table-wrap"><table class="tbl">
            <thead><tr><th>#</th><th>级别</th><th>题目</th><th>你的答案</th><th>正确答案</th><th>结果</th></tr></thead>
            <tbody>
              ${answers.map((a, i) => `
                <tr>
                  <td class="muted">${i + 1}</td>
                  <td>L${a.level}</td>
                  <td style="max-width:320px">${escapeHtml(String(a.prompt || '').replace(/\n/g, ' ').slice(0, 90))}</td>
                  <td class="en">${escapeHtml(a.response || '—')}</td>
                  <td class="en">${escapeHtml(a.expected || '—')}</td>
                  <td>${a.correct ? '<span class="sym-ok">✓</span>' : a.hintUsed ? '<span class="sym-hint">△</span>' : '<span class="sym-no">×</span>'}</td>
                </tr>`).join('') || '<tr class="empty-row"><td colspan="6">没有明细</td></tr>'}
            </tbody>
          </table></div>`,
      });
    });
  });
}

function moduleName(m) {
  return { word: '单词训练', sentence: '句子训练', grammar: '语法专项', comprehensive: '综合练习', game: '游戏', review: '复习' }[m] || m;
}
